// components/boats/RequestBoatForm.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, getDocs, getDocsFromServer, limit, orderBy, serverTimestamp, Timestamp, query, where } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { getAuth } from 'firebase/auth';
import { addDaysToDateISO, todayUKDateISO, ukDateISOToUTCStart } from '../../../../utils/ukDates';
import { preferredBoatStartTime } from './boatRequestDefaults';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';

// ── Date / time helpers ───────────────────────────────────────────────────────
function atMidnight(dateISO) {
  return ukDateISOToUTCStart(dateISO);
}

// Time helpers
function hhmmToMinutes(hhmm) {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + (m || 0);
}
function minutesToHHMM(mins) {
  const m = Math.max(0, Math.min(mins, 23 * 60 + 59));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

// Seats (optional filter)
function seatsForBoat(b) {
  if (!b) return null;
  if (Number.isFinite(b.seats)) return b.seats;
  const t = String(b.type || b.name || '').toLowerCase();
  const m = t.match(/(^|\D)(\d)(x|\+|-)?/);
  if (m) return Number(m[2]);
  if (t.includes('eight')) return 8;
  if (t.includes('quad') || t.includes('four')) return 4;
  if (t.includes('double') || t.includes('pair') || t.includes('2-')) return 2;
  if (t.includes('single') || t.includes('scull') || t.includes('1x')) return 1;
  return null;
}
function registrationNumberForBoat(b) {
  if (!b || typeof b.registrationNumber !== 'string') return '';
  return b.registrationNumber.trim();
}
function toFiniteNumberOrNull(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
function crewWeightRangeForBoat(b) {
  const min = toFiniteNumberOrNull(b?.crewWeightMinKg);
  const max = toFiniteNumberOrNull(b?.crewWeightMaxKg);
  if (min !== null && max !== null && min > max) {
    return {
      min: max,
      max: min
    };
  }
  return {
    min,
    max
  };
}
function crewWeightLabelForBoat(b) {
  const range = crewWeightRangeForBoat(b);
  if (range.min !== null && range.max !== null) return `Crew ${range.min}-${range.max}kg`;
  if (range.min !== null) return `Crew ${range.min}+kg`;
  if (range.max !== null) return `Crew <=${range.max}kg`;
  return '';
}
function formatWeight(value) {
  const rounded = Math.round(Number(value) * 10) / 10;
  if (!Number.isFinite(rounded)) return '';
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}
function avgCrewWeightLabelForBoat(b) {
  const range = avgCrewWeightRangeForBoat(b);
  const seats = seatsForBoat(b);
  if (!seats || seats <= 0) return '';
  if (range.min === null && range.max === null) return '';
  if (range.min !== null && range.max !== null) {
    return `Avg ${formatWeight(range.min)}-${formatWeight(range.max)}kg/person`;
  }
  if (range.min !== null) return `Avg ${formatWeight(range.min)}+kg/person`;
  return `Avg <=${formatWeight(range.max)}kg/person`;
}
function avgCrewWeightRangeForBoat(b) {
  const totalRange = crewWeightRangeForBoat(b);
  const seats = seatsForBoat(b);
  if (!seats || seats <= 0) return {
    min: null,
    max: null
  };
  const min = totalRange.min !== null ? totalRange.min / seats : null;
  const max = totalRange.max !== null ? totalRange.max / seats : null;
  return {
    min,
    max
  };
}
function normalizeMemberTags(rawTags) {
  if (Array.isArray(rawTags)) {
    return Array.from(new Set(rawTags.filter(tag => typeof tag === 'string' && tag.trim()).map(tag => tag.trim())));
  }
  if (rawTags && typeof rawTags === 'object') {
    return Array.from(new Set(Object.entries(rawTags).filter(([tagName, isAssigned]) => typeof tagName === 'string' && tagName.trim() && Boolean(isAssigned)).map(([tagName]) => tagName.trim())));
  }
  return [];
}
function compareBoatsByTypeThenName(a, b) {
  const typeCompare = (a?.type || '').localeCompare(b?.type || '', undefined, {
    sensitivity: 'base'
  });
  if (typeCompare !== 0) return typeCompare;
  return (a?.name || '').localeCompare(b?.name || '', undefined, {
    sensitivity: 'base'
  });
}
const ACTIVE_BOOKING_STATUSES = new Set(['reserved', 'allocated', 'confirmed', 'active']);
function timeOverlaps(aFrom, aTo, bFrom, bTo) {
  const toMins = t => {
    if (!t) return null;
    const [hh, mm] = String(t).split(':').map(Number);
    return hh * 60 + (mm || 0);
  };
  const aF = toMins(aFrom),
    aT = toMins(aTo);
  const bF = toMins(bFrom),
    bT = toMins(bTo);
  if (aF == null || aT == null || bF == null || bT == null) return true;
  return !(aT <= bF || bT <= aF);
}

// ── Drag & Drop ranking list (no external deps) ───────────────────────────────
function PreferenceRanking({
  preferences,
  setPreferences,
  boats
}) {
  const [dragIndex, setDragIndex] = useState(null);
  const actionButtonStyle = {
    minWidth: 30,
    padding: '0.2rem 0.45rem',
    lineHeight: 1
  };
  const idToBoat = useMemo(() => {
    const map = new Map();
    boats.forEach(b => map.set(b.id, b));
    return map;
  }, [boats]);
  const boatLabel = id => {
    const b = idToBoat.get(id);
    if (!b) return id;
    const seatTxt = seatsForBoat(b) ? `• ${seatsForBoat(b)}-seat` : '';
    const weightTxt = crewWeightLabelForBoat(b) ? `• ${crewWeightLabelForBoat(b)}` : '';
    const avgWeightTxt = avgCrewWeightLabelForBoat(b) ? `• ${avgCrewWeightLabelForBoat(b)}` : '';
    const regTxt = registrationNumberForBoat(b) ? `• Reg ${registrationNumberForBoat(b)}` : '';
    return [b?.name || 'Unnamed', b?.type ? `(${b.type})` : '', seatTxt, weightTxt, avgWeightTxt, regTxt].filter(Boolean).join(' ');
  };
  const movePref = (from, delta) => {
    const to = from + delta;
    if (to < 0 || to >= preferences.length) return;
    setPreferences(prev => {
      const arr = [...prev];
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      return arr;
    });
  };
  const onDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };
  const onDragOver = (e, overIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === overIndex) return;
    setPreferences(prev => {
      const arr = [...prev];
      const [m] = arr.splice(dragIndex, 1);
      arr.splice(overIndex, 0, m);
      return arr;
    });
    setDragIndex(overIndex);
  };
  const onDragEnd = () => setDragIndex(null);
  if (!preferences.length) return null;
  return <div className="form-section" aria-label="Boat ranking">
      <h3 style={{
      marginBottom: 8
    }}>My ranked preferences</h3>
      <ul className="prefs-list" style={{
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'grid',
      gap: 8
    }}>
        {preferences.map((id, idx) => <li key={id} draggable onDragStart={e => onDragStart(e, idx)} onDragOver={e => onDragOver(e, idx)} onDragEnd={onDragEnd} style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        border: '1px solid var(--border, #ddd)',
        borderRadius: 10,
        background: '#fff',
        boxShadow: dragIndex === idx ? '0 2px 10px rgba(0,0,0,0.08)' : 'none',
        cursor: 'grab'
      }} aria-label={`Preference ${idx + 1}: ${boatLabel(id)}`}>
            <span style={{
          fontWeight: 600,
          width: 26,
          textAlign: 'right'
        }}>{idx + 1}.</span>
            <span style={{
          flex: 1
        }}>{boatLabel(id)}</span>

            <div style={{
          display: 'flex',
          gap: 6
        }}>
              <button type="button" className="button button-secondary button--xs" style={actionButtonStyle} title="Move up" aria-label="Move up" disabled={idx === 0} onClick={() => movePref(idx, -1)}>
                ↑
              </button>
              <button type="button" className="button button-secondary button--xs" style={actionButtonStyle} title="Move down" aria-label="Move down" disabled={idx === preferences.length - 1} onClick={() => movePref(idx, +1)}>
                ↓
              </button>
              <button type="button" className="button button-ghost button--xs" style={actionButtonStyle} title="Remove from preferences" aria-label="Remove" onClick={() => setPreferences(prev => prev.filter(x => x !== id))}>
                ✕
              </button>
            </div>
          </li>)}
      </ul>
      <p className="text-muted" style={{
      marginTop: 6,
      fontSize: '0.9rem'
    }}>
        Drag to reorder. The first item is your top choice.
      </p>
    </div>;
}
export default function RequestBoatForm({
  isOpen,
  onClose,
  onSave,
  rules = {},
  resources,
  userTags = [],
  teamId,
  memberId
}) {
  const auth = getAuth();
  const authUid = auth.currentUser?.uid || null;
  const fromTimeEditedRef = useRef(false);
  const [date, setDate] = useState(todayUKDateISO());
  const [fromTime, setFromTime] = useState('08:00');

  // ── New: auto-bump if today ──────────────────────────────────────────────
  useEffect(() => {
    const todayISO = todayUKDateISO();
    if (date !== todayISO) return;
    const now = new Date();
    const mins = now.getHours() * 60 + now.getMinutes() + 6; // +6min clears the +5min guard
    const rounded = Math.ceil(mins / 15) * 15; // round up to next 15-min slot
    const hh = String(Math.floor(rounded / 60)).padStart(2, '0');
    const mm = String(rounded % 60).padStart(2, '0');
    const candidate = `${hh}:${mm}`;
    setFromTime(prev => hhmmToMinutes(prev) < hhmmToMinutes(candidate) ? candidate : prev);
  }, [date, fromTime]);
  const DURATION_PRESETS = [{
    label: '30 minutes',
    value: 30
  }, {
    label: '1 hour',
    value: 60
  }, {
    label: '1 hour 30 minutes',
    value: 90
  }, {
    label: '2 hours',
    value: 120
  }, {
    label: '2 hours 30 minutes',
    value: 150
  }, {
    label: '3 hours',
    value: 180
  }, {
    label: 'Custom…',
    value: 'custom'
  }];
  const [durationChoice, setDurationChoice] = useState(60);
  const [customDurationMin, setCustomDurationMin] = useState(75);
  const durationMin = durationChoice === 'custom' ? Number(customDurationMin || 0) : Number(durationChoice || 0);
  const [preferences, setPreferences] = useState([]); // ordered array (ranked)
  const [notes, setNotes] = useState('');
  const [loadedBoats, setLoadedBoats] = useState([]);
  const [loadingBoats, setLoadingBoats] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uiError, setUiError] = useState('');
  const [seatFilter, setSeatFilter] = useState('any');
  const [crewWeightFilterKg, setCrewWeightFilterKg] = useState('');
  const [weightFilterMode, setWeightFilterMode] = useState('avgRower');
  const [boatSearch, setBoatSearch] = useState('');
  const [availableBoatIds, setAvailableBoatIds] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const actionButtonStyle = {
    minHeight: 28,
    padding: '0.24rem 0.55rem',
    fontSize: '0.78rem',
    fontWeight: 500,
    lineHeight: 1.1,
    border: '1px solid currentColor',
    borderRadius: '0.5rem',
    background: 'transparent',
    color: 'inherit',
    opacity: 0.9,
    boxShadow: 'none',
    transform: 'none'
  };
  const toTime = useMemo(() => {
    const startMins = hhmmToMinutes(fromTime);
    const endMins = startMins + (Number.isFinite(durationMin) ? durationMin : 0);
    return minutesToHHMM(endMins);
  }, [fromTime, durationMin]);
  const crossesMidnight = useMemo(() => {
    const startMins = hhmmToMinutes(fromTime);
    const endMins = startMins + (Number.isFinite(durationMin) ? durationMin : 0);
    return endMins > 24 * 60;
  }, [fromTime, durationMin]);
  function isRetrospective() {
    const today = todayUKDateISO();
    if (date < today) return true;
    if (date > today) return false;
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const startMins = hhmmToMinutes(fromTime);
    return startMins < nowMins + 5;
  }
  useEffect(() => {
    if (!isOpen) return;
    setPreferences([]);
    setNotes('');
    setDate(todayUKDateISO());
    fromTimeEditedRef.current = false;
    setFromTime('08:00');
    setDurationChoice(60);
    setCustomDurationMin(75);
    setSeatFilter('any');
    setCrewWeightFilterKg('');
    setWeightFilterMode('avgRower');
    setBoatSearch('');
    setUiError('');
    setAvailabilityError('');
    setAvailableBoatIds(null);
    if (Array.isArray(resources)) {
      setLoadedBoats(resources);
      return;
    }
    if (!teamId) {
      setLoadedBoats([]);
      return;
    }
    setLoadingBoats(true);
    const colRef = collection(db, `teams/${teamId}/boats`);
    const unsubscribe = safeOnSnapshot(colRef, snap => {
      const boats = snap.docs.map(d => ({
          id: d.id,
          ...d.data()
      }));
      setLoadedBoats(boats);
      setLoadingBoats(false);
    }, () => {
      setUiError('Could not load the club boats. Check your connection and try again.');
      setLoadingBoats(false);
    }, { label: 'rowing-request-boat-fleet', teamId });
    return () => unsubscribe();
  }, [isOpen, teamId, resources]);
  useEffect(() => {
    if (!isOpen || !teamId || !memberId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const historyQuery = query(
          collection(db, `teams/${teamId}/boatRequests`),
          where('memberId', '==', memberId),
          orderBy('date', 'desc'),
          limit(75)
        );
        const historySnap = await getDocs(historyQuery);
        if (cancelled || fromTimeEditedRef.current) return;
        const preferredTime = preferredBoatStartTime(historySnap.docs.map(docSnap => docSnap.data()));
        setFromTime(preferredTime);
      } catch {
        // Keep the 08:00 fallback if history is unavailable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, memberId, teamId]);
  const boatsSource = Array.isArray(resources) ? resources : loadedBoats;
  const activeBoats = useMemo(() => (boatsSource || []).filter(r => (r?.status || 'active') !== 'maintenance'), [boatsSource]);
  const normalizedMemberTags = useMemo(() => normalizeMemberTags(userTags), [userTags]);
  const memberTagSet = useMemo(() => new Set(normalizedMemberTags), [normalizedMemberTags]);
  const eligibleBoats = useMemo(() => {
    return activeBoats.filter(b => {
      const allowed = Array.isArray(b?.tagsAllowed) ? b.tagsAllowed : [];
      if (allowed.length === 0) return true;
      return allowed.some(t => memberTagSet.has(t));
    });
  }, [activeBoats, memberTagSet]);
  useEffect(() => {
    if (!isOpen || !teamId) return;
    if (!date || !fromTime || !toTime) return;
    if (!eligibleBoats.length) {
      setAvailableBoatIds(new Set());
      setCheckingAvailability(false);
      setAvailabilityError('');
      return;
    }
    let cancelled = false;
    setCheckingAvailability(true);
    setAvailabilityError('');
    const dayStart = atMidnight(date);
    const dayEnd = ukDateISOToUTCStart(addDaysToDateISO(date, 1));
    (async () => {
      try {
        const dayStartTs = Timestamp.fromDate(dayStart);
        const dayEndTs = Timestamp.fromDate(dayEnd);
        const checks = await Promise.all(eligibleBoats.map(async boat => {
          const q = query(collection(db, `teams/${teamId}/boats/${boat.id}/bookings`), where('date', '>=', dayStartTs), where('date', '<', dayEndTs));
          const snap = await getDocsFromServer(q);
          const hasClash = snap.docs.some(docSnap => {
            const booking = docSnap.data() || {};
            const status = String(booking.status || 'reserved').toLowerCase();
            if (!ACTIVE_BOOKING_STATUSES.has(status)) return false;
            return timeOverlaps(fromTime, toTime, booking.fromTime || null, booking.toTime || null);
          });
          return {
            boatId: boat.id,
            available: !hasClash
          };
        }));
        if (cancelled) return;
        setAvailableBoatIds(new Set(checks.filter(c => c.available).map(c => c.boatId)));
      } catch (e) {
        if (cancelled) return;
        setAvailabilityError('Could not check live availability. Showing all eligible boats.');
        setAvailableBoatIds(new Set(eligibleBoats.map(b => b.id)));
      } finally {
        if (!cancelled) setCheckingAvailability(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId, date, fromTime, toTime, eligibleBoats, memberId]);
  const availabilityFilteredBoats = useMemo(() => {
    if (!availableBoatIds) return eligibleBoats;
    return eligibleBoats.filter(b => availableBoatIds.has(b.id));
  }, [eligibleBoats, availableBoatIds]);
  const weightFilteredBoats = useMemo(() => {
    const raw = String(crewWeightFilterKg || '').trim();
    if (!raw) return availabilityFilteredBoats;
    const target = Number(raw);
    if (!Number.isFinite(target) || target <= 0) return [];
    return availabilityFilteredBoats.filter(b => {
      const range = weightFilterMode === 'crewTotal' ? crewWeightRangeForBoat(b) : avgCrewWeightRangeForBoat(b);
      if (range.min === null && range.max === null) return false;
      if (range.min !== null && target < range.min) return false;
      if (range.max !== null && target > range.max) return false;
      return true;
    });
  }, [availabilityFilteredBoats, crewWeightFilterKg, weightFilterMode]);

  // Build seat options once and reuse
  const seatOptions = useMemo(() => {
    const set = new Set();
    weightFilteredBoats.forEach(b => {
      const s = seatsForBoat(b);
      if (s) set.add(s);
    });
    return ['any', ...Array.from(set).sort((a, b) => a - b)];
  }, [weightFilteredBoats]);
  const seatFilteredBoats = useMemo(() => {
    if (seatFilter === 'any') return weightFilteredBoats;
    const target = Number(seatFilter);
    return weightFilteredBoats.filter(b => seatsForBoat(b) === target);
  }, [weightFilteredBoats, seatFilter]);
  useEffect(() => {
    if (seatFilter === 'any') return;
    const stillValid = seatOptions.some(opt => String(opt) === String(seatFilter));
    if (!stillValid) setSeatFilter('any');
  }, [seatFilter, seatOptions]);
  const sortedVisibleBoats = useMemo(() => [...seatFilteredBoats].sort(compareBoatsByTypeThenName), [seatFilteredBoats]);
  const displayedBoats = useMemo(() => {
    const term = boatSearch.trim().toLowerCase();
    if (!term) return sortedVisibleBoats;
    return sortedVisibleBoats.filter(b => {
      const hay = `${b?.name || ''} ${b?.type || ''} ${registrationNumberForBoat(b) || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [sortedVisibleBoats, boatSearch]);
  const hasAnyMemberTags = normalizedMemberTags.length > 0;
  const hasTagRestrictedBoats = activeBoats.some(boat => {
    const allowed = Array.isArray(boat?.tagsAllowed) ? boat.tagsAllowed : [];
    return allowed.length > 0;
  });
  const noTagMatchForAnyBoat = activeBoats.length > 0 && hasTagRestrictedBoats && eligibleBoats.length === 0;
  const emptyBoatMessage = useMemo(() => {
    if (loadingBoats || displayedBoats.length > 0) return '';
    if (activeBoats.length === 0) return 'No active boats are configured.';
    if (noTagMatchForAnyBoat) {
      if (!hasAnyMemberTags) {
        return 'You do not currently have any member tags, and all active boats require tags. Ask an admin to assign your tags.';
      }
      return 'None of your member tags match the boats you can book. Ask an admin to review your tags or boat access tags.';
    }
    if (eligibleBoats.length > 0 && availabilityFilteredBoats.length === 0) {
      return 'No eligible boats are currently available in this time slot.';
    }
    if (availabilityFilteredBoats.length > 0 && weightFilteredBoats.length === 0) {
      return 'No boats match your weight filter.';
    }
    if (weightFilteredBoats.length > 0 && seatFilteredBoats.length === 0) {
      return 'No boats match your seat filter.';
    }
    if (sortedVisibleBoats.length > 0 && boatSearch.trim()) {
      return 'No boats match your search term.';
    }
    return 'No eligible boats match this filter.';
  }, [loadingBoats, displayedBoats.length, activeBoats.length, noTagMatchForAnyBoat, hasAnyMemberTags, eligibleBoats.length, availabilityFilteredBoats.length, weightFilteredBoats.length, seatFilteredBoats.length, sortedVisibleBoats.length, boatSearch]);
  useEffect(() => {
    // Prune any selected prefs that are no longer visible due to filter changes
    const visibleIds = new Set(sortedVisibleBoats.map(b => b.id));
    setPreferences(prev => prev.filter(id => visibleIds.has(id)));
  }, [sortedVisibleBoats]);
  if (!isOpen) return null;

  // Keep preferences as an ordered array (selection order = ranking)
  const togglePref = id => {
    if (!id) return;
    setPreferences(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      return [...prev, id];
    });
  };
  const addVisibleToPreferences = () => {
    const ids = displayedBoats.map(b => b?.id).filter(Boolean);
    if (!ids.length) return;
    setPreferences(prev => {
      const next = [...prev];
      ids.forEach(id => {
        if (!next.includes(id)) next.push(id);
      });
      return next;
    });
  };
  const clearPreferences = () => setPreferences([]);
  async function createPendingRequest() {
    const start = atMidnight(date);
    const payload = {
      memberId,
      date: Timestamp.fromDate(start),
      fromTime,
      toTime,
      preferences,
      status: 'pending',
      createdAt: serverTimestamp(),
      notes: notes || null,
      teamId
    };
    const colRef = collection(db, `teams/${teamId}/boatRequests`);
    const ref = await addDoc(colRef, payload);
    return ref.id;
  }
  const submit = async e => {
    e.preventDefault();
    setUiError('');
    if (!teamId || !memberId) {
      setUiError('Missing team or member information.');
      return;
    }
    if (authUid && memberId !== authUid) {
      setUiError('You can only request a boat for your own account.');
      return;
    }
    if (preferences.length === 0) {
      setUiError('Please select at least one boat.');
      return;
    }
    if (!Number.isFinite(durationMin) || durationMin <= 0) {
      setUiError('Please choose a valid duration.');
      return;
    }
    if (crossesMidnight) {
      setUiError('Duration cannot cross midnight. Please shorten or start earlier.');
      return;
    }
    if (isRetrospective()) {
      setUiError('You cannot book in the past. Choose a future start time.');
      return;
    }
    setSubmitting(true);
    try {
      const pendingId = await createPendingRequest();
      onSave?.({
        date,
        fromTime,
        toTime,
        preferences,
        mode: 'requested',
        pendingId
      });
      onClose?.();
    } catch (e) {
      setUiError(e?.code === 'permission-denied'
        ? 'Your account is not currently allowed to create boat requests for this team. Ask a team administrator to check your membership or boat-booking access.'
        : `Submit failed: ${e?.code || e?.name || 'error'}`);
    } finally {
      setSubmitting(false);
    }
  };
  const todayISO = todayUKDateISO();
  const canSubmit = preferences.length > 0 && !loadingBoats && !submitting && Number.isFinite(durationMin) && durationMin > 0 && !crossesMidnight;
  const weightFilterLabel = weightFilterMode === 'crewTotal' ? 'Total crew weight (kg, optional)' : 'Avg rower weight (kg, optional)';
  const weightFilterHelpText = weightFilterMode === 'crewTotal' ? 'Matches boats where the crew-total range includes this value.' : 'Matches boats where avg per-person range includes this value.';
  const unavailableBoatCount = Math.max(0, eligibleBoats.length - availabilityFilteredBoats.length);
  return <form onSubmit={submit} className="modal-form">
      <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 12,
      marginBottom: 12
    }}>
        <h2 style={{
        margin: 0
      }}>Request a Boat</h2>
        <button type="button" className="button button--xs" style={actionButtonStyle} onClick={() => setShowHelp(prev => !prev)} aria-label="How boat booking works">
          ❓
        </button>
      </div>
      <div className="form-section boat-request-panel" style={{
      background: '#f8fafc',
      marginBottom: 12
    }}>
        <div style={{
        display: 'grid',
        gap: 4
      }}>
          <strong>Booking a boat directly for a session.</strong>
          <div className="text-muted">Organising multiple crews from an RSVP event? Outings may be a better fit.</div>
        </div>
      </div>
      {showHelp && <div className="form-section boat-request-panel" style={{
      background: '#f8fafc'
    }}>
          <div style={{
        display: 'grid',
        gap: 6
      }}>
            <strong>How boat booking works</strong>
            <div className="text-muted">Pick your session time first, then rank the boats you would be happy to use. Each submission requests one boat.</div>
            <div className="text-muted">The list auto-hides boats that are already taken for that slot and any boats your tags do not let you book.</div>
            <div className="text-muted">If the slot is already inside the live booking window, the request can be allocated straight away. Otherwise it waits for the team’s release day.</div>
          </div>
        </div>}

      <section className="form-section boat-request-panel">
        <div className="boat-request-section-head">
          <h3>Session</h3>
          <span className="boat-request-summary-pill">Ends {toTime}</span>
        </div>

        <div className="boat-request-grid">
          <label className="form-label">
            Date
            <input type="date" value={date} min={todayISO} onChange={e => setDate(e.target.value)} />
          </label>

          <label className="form-label">
            Start time
            <input type="time" value={fromTime} onChange={e => {
              fromTimeEditedRef.current = true;
              setFromTime(e.target.value);
            }} />
          </label>

          <label className="form-label">
            Duration
            <select value={durationChoice} onChange={e => setDurationChoice(e.target.value === 'custom' ? 'custom' : Number(e.target.value))}>
              {DURATION_PRESETS.map(opt => <option key={opt.label} value={opt.value}>{opt.label}</option>)}
            </select>
          </label>

          {durationChoice === 'custom' && <label className="form-label">
              Custom duration (minutes)
              <input type="number" min={5} max={360} step={5} value={customDurationMin} onChange={e => setCustomDurationMin(e.target.value)} />
              <span className="help-text">Between 5 and 360 minutes.</span>
            </label>}
        </div>

        {crossesMidnight && <div className="text-warning" style={{
        marginTop: 4
      }}>
            The selected duration runs past midnight. Please adjust your times.
          </div>}
      </section>

      <section className="form-section boat-request-panel">
        <div className="boat-request-section-head">
          <h3>Filters</h3>
          <span className="text-muted">Narrow the list before ranking preferences</span>
        </div>

        <div className="boat-request-grid">
          <label className="form-label">
            Seats (optional)
            <select value={seatFilter} onChange={e => setSeatFilter(e.target.value)}>
              {seatOptions.map(opt => <option key={String(opt)} value={String(opt)}>
                  {opt === 'any' ? 'Any' : `${opt}-seat`}
                </option>)}
            </select>
            <span className="help-text">Filter boats by crew size.</span>
          </label>

          <label className="form-label">
            Weight filter mode
            <select value={weightFilterMode} onChange={e => setWeightFilterMode(e.target.value)}>
              <option value="avgRower">Average rower</option>
              <option value="crewTotal">Crew total</option>
            </select>
            <span className="help-text">Choose how to enter weight.</span>
          </label>

          <label className="form-label">
            {weightFilterLabel}
            <input type="number" min={1} step={1} value={crewWeightFilterKg} onChange={e => setCrewWeightFilterKg(e.target.value)} placeholder={weightFilterMode === 'crewTotal' ? 'e.g. 310' : 'e.g. 78'} />
            <span className="help-text">{weightFilterHelpText}</span>
          </label>
        </div>

        <div className="boat-request-availability-note text-muted">
          {checkingAvailability ? 'Checking live availability for this slot…' : `Auto-filtering to currently available boats for this slot${unavailableBoatCount > 0 ? ` (${unavailableBoatCount} unavailable hidden)` : ''}.`}
        </div>
        {!!availabilityError && <div className="text-warning" style={{
        marginTop: 4
      }}>
            {availabilityError}
          </div>}
      </section>

      <div className="form-section">
        <div className="boat-request-pref-head">
          <h3>Boat preferences</h3>
          <div className="text-muted boat-request-pref-meta">
            {loadingBoats ? 'Loading boats...' : `${preferences.length} selected • ${sortedVisibleBoats.length} eligible`}
          </div>
        </div>

        <div className="boat-request-search-row">
          <label className="form-label boat-request-search-label">
            Search boats
            <input value={boatSearch} onChange={e => setBoatSearch(e.target.value)} placeholder="Search name, type or reg" />
          </label>
          <button type="button" className="button button--xs" style={actionButtonStyle} onClick={addVisibleToPreferences} disabled={loadingBoats || displayedBoats.length === 0} title="Add all shown boats to preferences">
            Select shown
          </button>
          <button type="button" className="button button--xs" style={actionButtonStyle} onClick={clearPreferences} disabled={preferences.length === 0}>
            Clear selected
          </button>
        </div>

        {/* Chips picker */}
        <div className="boat-request-chip-grid">
          {!loadingBoats && displayedBoats.map(b => {
          const id = b?.id;
          const checked = id ? preferences.includes(id) : false;
          const label = [b?.name || 'Unnamed', b?.type ? `(${b.type})` : '', seatsForBoat(b) ? `• ${seatsForBoat(b)}-seat` : '', crewWeightLabelForBoat(b) ? `• ${crewWeightLabelForBoat(b)}` : '', avgCrewWeightLabelForBoat(b) ? `• ${avgCrewWeightLabelForBoat(b)}` : '', registrationNumberForBoat(b) ? `• Reg ${registrationNumberForBoat(b)}` : ''].filter(Boolean).join(' ');
          return <label key={id} className="chip" style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '8px 10px',
            borderRadius: 10,
            border: '1px solid rgba(0,0,0,0.12)',
            background: checked ? 'rgba(59,130,246,0.1)' : '#fff',
            fontSize: '0.88rem',
            lineHeight: 1.3
          }}>
                <input type="checkbox" checked={checked} onChange={() => togglePref(id)} disabled={!id} style={{
              marginTop: 2
            }} />
                {label}
              </label>;
        })}
          {!loadingBoats && displayedBoats.length === 0 && <div className="text-muted">{emptyBoatMessage}</div>}
        </div>

        {/* Ranked list */}
        <PreferenceRanking preferences={preferences} setPreferences={setPreferences} boats={sortedVisibleBoats} />
      </div>

      <label className="form-label">
        Notes (optional)
        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any constraints or crew info…" />
      </label>

      {!!uiError && <div className="text-error" style={{
      marginBottom: 8
    }}>
          {uiError}
        </div>}

      <div className="form-actions">
        <button type="button" className="button button--xs" style={actionButtonStyle} onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="button button--xs" style={canSubmit ? actionButtonStyle : {
        ...actionButtonStyle,
        opacity: 0.5,
        cursor: 'not-allowed'
      }} disabled={!canSubmit}>
          {submitting ? 'Submitting…' : 'Request boat'}
        </button>
      </div>
    </form>;
}
